import { type ReactNode } from "react";
import { ChevronDown, Hotel, Loader2 } from "lucide-react";
import { Property } from "./FetchProperties";
import Sheet from "@/components/ui/sheet";
import {
  DropdownFooter,
  PropertyListContent,
  SearchField,
} from "./PropertyListRows";

interface MultiSelectMobileSheetProps {
  loading: boolean;
  triggerLabel: string | null;
  dropdownOpen: boolean;
  onTriggerClick: () => void;
  onClose: () => void;
  dropdownSearch: string;
  onSearchChange: (value: string) => void;
  filteredPortfolio: Property[];
  filteredNonPortfolio: Property[];
  showSelectAllPortfolio: boolean;
  pendingCount: number;
  isChecked: (property: Property) => boolean;
  onToggleProperty: (property: Property) => void;
  onSelectAllPortfolio: () => void;
  onClearPending: () => void;
  onApply: () => void;
  selectionLimitWarning: ReactNode;
}

const MultiSelectMobileSheet = ({
  loading,
  triggerLabel,
  dropdownOpen,
  onTriggerClick,
  onClose,
  dropdownSearch,
  onSearchChange,
  filteredPortfolio,
  filteredNonPortfolio,
  showSelectAllPortfolio,
  pendingCount,
  isChecked,
  onToggleProperty,
  onSelectAllPortfolio,
  onClearPending,
  onApply,
  selectionLimitWarning,
}: MultiSelectMobileSheetProps) => {
  return (
    <>
      <button
        type="button"
        onClick={onTriggerClick}
        aria-haspopup="dialog"
        aria-expanded={dropdownOpen}
        className="inline-flex max-w-full min-w-0 cursor-pointer select-none items-center gap-[3px] rounded py-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
      >
        {loading ? (
          <Loader2
            size={14}
            className="shrink-0 animate-spin"
            style={{ color: "rgba(255,255,255,0.6)" }}
          />
        ) : (
          <Hotel
            size={15}
            style={{ color: "rgba(255,255,255,0.65)" }}
            className="shrink-0"
          />
        )}
        <span
          className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-[0.875rem]"
          style={{
            fontWeight: triggerLabel ? 500 : 400,
            color: triggerLabel ? "#fff" : "rgba(255,255,255,0.5)",
          }}
        >
          {triggerLabel ?? "Select properties"}
        </span>
        <ChevronDown
          size={14}
          style={{ color: "rgba(255,255,255,0.55)" }}
          className="shrink-0"
        />
      </button>

      <Sheet
        open={dropdownOpen}
        onOpenChange={(next) => {
          if (!next) onClose();
        }}
        title="Select Properties"
        side="bottom"
        className="max-h-[78vh] rounded-t-[16px] bg-white"
      >
        <div className="flex shrink-0 justify-center pt-[6px] pb-0.5">
          <div className="h-1 w-9 rounded-full bg-slate-300" />
        </div>

        <div className="shrink-0 border-b border-slate-100 px-2 pt-0.5 pb-1.5">
          <p className="mt-0 mb-[5px] text-base font-bold text-slate-800">
            Select Properties
          </p>
          <SearchField
            value={dropdownSearch}
            onChange={onSearchChange}
            isMobile
          />
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden py-0.5">
          <PropertyListContent
            isMobile
            filteredPortfolio={filteredPortfolio}
            filteredNonPortfolio={filteredNonPortfolio}
            showSelectAllPortfolio={showSelectAllPortfolio}
            isChecked={isChecked}
            onToggleProperty={onToggleProperty}
            onSelectAllPortfolio={onSelectAllPortfolio}
          />
        </div>

        {selectionLimitWarning}
        <DropdownFooter
          pendingCount={pendingCount}
          onClear={onClearPending}
          onApply={onApply}
          isMobile
        />
      </Sheet>
    </>
  );
};

export default MultiSelectMobileSheet;
